import { useTranslation } from 'react-i18next'
import Header from '@/components/layout/header'
import Footer from '@/components/layout/footer'
import Container from '@/components/ui/container'
import Button from '@/components/ui/button'
import Icon from '@/components/ui/icon'
import Link from '@/components/ui/link'
import Seo from '@/components/seo/seo'
import NotFound from '@/pages/not-found'
import { articleSchema, breadcrumbSchema } from '@/components/seo/json-ld'
import { resolveLang } from '@/lib/site'

const ARTICLE_IMAGES = [
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-1.png',
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-2.png',
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-3.png',
]

interface Takeaway {
  icon: string
  title: string
  description: string
}

interface Metric {
  value: string
  label: string
}

interface ArticleData {
  slug: string
  category: string
  title: string
  subtitle: string
  readTime: string
  keyInsight: string
  content: string[]
  takeaways: Takeaway[]
  metrics: Metric[]
}

interface ArticlePageProps {
  slug: string
}

export default function ArticlePage({ slug }: ArticlePageProps) {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)

  const articles = t('articlePages', { returnObjects: true }) as ArticleData[]
  const index = articles.findIndex((a) => a.slug === slug)
  const article = articles[index]

  if (!article) return <NotFound />

  const path = `/article/${article.slug}`
  const image = ARTICLE_IMAGES[index]

  return (
    <>
      <Seo
        title={article.title}
        description={article.subtitle}
        path={path}
        type="article"
        image={image}
        jsonLd={[
          breadcrumbSchema(lang, [
            { name: 'Bravic', path: '/' },
            { name: 'Insights', path: '/#insights' },
            { name: article.title, path },
          ]),
          articleSchema(lang, {
            headline: article.title,
            description: article.subtitle,
            path,
            image,
          }),
        ]}
      />
      <Header />
      <main className="relative overflow-hidden">
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-end pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              className="w-full h-full object-cover opacity-30 grayscale"
              src={ARTICLE_IMAGES[index]}
              alt={article.title}
            />
            <div className="absolute inset-0 bg-linear-to-t from-surface via-surface/80 to-surface/40" />
          </div>
          <Container className="relative z-10">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-primary text-sm font-bold uppercase tracking-widest mb-8 hover:text-primary-fixed transition-colors"
            >
              <span>←</span>
              <span>{t('articlePage.backLabel')}</span>
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-primary font-bold text-xs uppercase tracking-[0.3em]">
                {article.category}
              </span>
              <span className="text-on-surface-variant/60 text-xs">•</span>
              <span className="text-on-surface-variant text-xs font-medium flex items-center gap-1">
                <Icon name="schedule" className="text-sm" />
                {article.readTime}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95] text-on-surface max-w-4xl mb-6">
              {article.title}
            </h1>
            <p className="text-on-surface-variant text-xl max-w-2xl">{article.subtitle}</p>
          </Container>
        </section>

        {/* Key Metrics */}
        <section className="py-16 bg-surface border-b border-outline-variant/20">
          <Container>
            <div className="text-center mb-10">
              <span className="text-primary font-bold text-xs uppercase tracking-[0.3em]">
                {t('articlePage.relatedMetrics')}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {article.metrics.map((metric, i) => (
                <div key={i} className="text-center">
                  <div className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-primary mb-2">
                    {metric.value}
                  </div>
                  <div className="text-on-surface-variant text-sm font-medium uppercase tracking-wider">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Key Insight */}
        <section className="py-16 bg-surface-container-lowest">
          <Container>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="lightbulb" className="text-2xl text-primary" />
                <span className="text-primary font-bold text-xs uppercase tracking-[0.3em]">
                  {t('articlePage.keyInsight')}
                </span>
              </div>
              <blockquote className="font-display text-xl md:text-2xl font-bold tracking-tight text-on-surface leading-snug pl-6 border-l-2 border-primary">
                {article.keyInsight}
              </blockquote>
            </div>
          </Container>
        </section>

        {/* Content */}
        <section className="py-20 bg-surface">
          <Container>
            <div className="max-w-3xl mx-auto space-y-8">
              {article.content.map((paragraph, i) => (
                <p
                  key={i}
                  className={`text-on-surface-variant text-lg leading-relaxed ${i === 0 ? 'text-xl font-medium text-on-surface' : ''}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </Container>
        </section>

        {/* Takeaways */}
        <section className="py-20 bg-surface-container-lowest">
          <Container>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-primary font-bold text-xs uppercase tracking-[0.3em] block mb-4">
                  {t('articlePage.takeaways')}
                </span>
                <div className="w-12 h-1 bg-primary mx-auto" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {article.takeaways.map((takeaway, i) => (
                  <div key={i} className="bg-surface-container p-8 border-t-2 border-primary">
                    <Icon name={takeaway.icon} className="text-3xl text-primary mb-4" />
                    <h3 className="font-display text-lg font-extrabold tracking-tight text-on-surface mb-3">
                      {takeaway.title}
                    </h3>
                    <p className="text-on-surface-variant text-sm leading-relaxed">
                      {takeaway.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="py-20 bg-surface">
          <Container className="text-center">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-on-surface mb-4 max-w-2xl mx-auto">
              {t('articlePage.ctaHeading')}
            </h2>
            <p className="text-on-surface-variant text-lg max-w-xl mx-auto mb-8">
              {t('articlePage.ctaDescription')}
            </p>
            <Link href="/contact">
              <Button variant="gradient" size="lg">
                {t('articlePage.ctaButton')}
              </Button>
            </Link>
          </Container>
        </section>
      </main>
      <Footer />
    </>
  )
}
